import React, { useCallback } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DateTime } from "luxon";
import type { FilterValue } from "hooks/useFilters";
import DatePickerInput from "../DatePickerInput";

export enum FilterSectionType {
  Text,
  Checkbox,
  Select,
  Date,
  ToggleGroup,
}

interface BaseFilterSectionProps {
  type: FilterSectionType;
  label: string;
  filterKey: string;
  setFilter?: (key: string, value: FilterValue) => void;
  value?: FilterValue;
}

interface TextFilterSectionProps extends BaseFilterSectionProps {
  type: FilterSectionType.Text;
}

interface CheckboxFilterSectionProps extends BaseFilterSectionProps {
  type: FilterSectionType.Checkbox;
}

interface SelectFilterSectionProps extends BaseFilterSectionProps {
  type: FilterSectionType.Select;
  selectItems?: { value: string | number; label: string }[];
  deselectOptionLabel?: string;
}

interface DateFilterSectionProps extends BaseFilterSectionProps {
  type: FilterSectionType.Date;
}

/**
 * Mutually exclusive options shown side by side. `label` names the group for assistive technology
 * rather than rendering — the buttons carry their own visible labels.
 */
interface ToggleGroupFilterSectionProps extends BaseFilterSectionProps {
  type: FilterSectionType.ToggleGroup;
  toggleItems: { value: string; label: string }[];
  /** Selected while the filter is unset. Choosing it clears the filter rather than setting it. */
  defaultValue: string;
}

export type FilterSectionFieldProps =
  | TextFilterSectionProps
  | CheckboxFilterSectionProps
  | SelectFilterSectionProps
  | DateFilterSectionProps
  | ToggleGroupFilterSectionProps;

// Equal-width segments, so a short option like "All" doesn't sit squeezed between longer ones.
// This lays the buttons out as grid columns rather than the flex row the group ships with: the
// group is sized by its own content, so there is never free space for `flex-grow` to hand out and
// the buttons keep their individual text widths. Equal `1fr` columns instead size every button to
// the widest label. Horizontal only — the flow would have to change for `orientation="vertical"`.
const toggleGroupStyles = {
  display: "inline-grid",
  gridAutoFlow: "column",
  gridAutoColumns: "1fr",
};

function FilterSection(props: FilterSectionFieldProps) {
  const { type, filterKey, label, setFilter, value } = props;

  const filterChanged = useCallback(
    (value: FilterValue) => {
      if (setFilter) {
        setFilter(filterKey, value);
      }
    },
    [setFilter, filterKey]
  );

  const handleChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let newValue: FilterValue = undefined;
      switch (type) {
        case FilterSectionType.Checkbox:
          newValue = event.target.checked;
          break;
        case FilterSectionType.Select:
        case FilterSectionType.Text:
          newValue = event.target.value;
          break;
        default:
          throw new Error("Unknown ");
      }
      filterChanged(newValue);
    },
    [filterChanged, type]
  );

  const handleDateFilterChanged = useCallback(
    (date: DateTime | null, keyboardInputValue?: string | undefined) => {
      filterChanged(date?.startOf("day"));
    },
    [filterChanged]
  );

  switch (props.type) {
    case FilterSectionType.Text:
      return <TextField fullWidth variant="standard" label={label} value={value ?? ""} onChange={handleChanged} />;
    case FilterSectionType.Checkbox:
      return (
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={!!value} value={value} onChange={handleChanged} />}
            label={label}
          />
        </FormGroup>
      );
    case FilterSectionType.Select:
      return (
        <Box
          component="form"
          sx={{
            minWidth: "25ch",
            width: "100%",
          }}
          noValidate
          autoComplete="off"
        >
          <TextField fullWidth variant="standard" select label={label} value={value ?? ""} onChange={handleChanged}>
            <MenuItem value="">
              <em>{props.deselectOptionLabel ?? "None"}</em>
            </MenuItem>
            {props.selectItems?.map((item, index) => (
              <MenuItem key={index} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      );
    case FilterSectionType.Date:
      return (
        <DatePickerInput
          label={label}
          value={value instanceof DateTime || typeof value === "string" ? value : undefined}
          onChange={handleDateFilterChanged}
          showLocalConversion={false}
        />
      );
    case FilterSectionType.ToggleGroup:
      return (
        <ToggleButtonGroup
          exclusive
          size="small"
          aria-label={label}
          sx={toggleGroupStyles}
          value={value ?? props.defaultValue}
          // MUI reports clicking the active button as a null selection. Honouring it would leave no
          // option in force, so it is ignored; the way back to the default is to pick it.
          onChange={(_, selected) => {
            if (selected !== null) {
              filterChanged(selected === props.defaultValue ? undefined : selected);
            }
          }}
        >
          {props.toggleItems.map((item) => (
            <ToggleButton key={item.value} value={item.value}>
              {item.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      );
  }
}

export default FilterSection;
